import type { LocaleContent } from "./types";
import { WEBSITE_BASE } from "../lib/siteBase";

export const de: LocaleContent = {
  lang: "de",
  localePath: `${WEBSITE_BASE}/`,
  altLocalePath: `${WEBSITE_BASE}/en/`,
  nav: {
    home: "Home",
    campsite: "Unser Campingplatz",
    gallery: "Galerie",
    hours: "Öffnungszeiten",
    prices: "Preise",
    directions: "Anfahrt",
    booking: "Online Buchung",
  },
  hero: {
    title: "Campingplatz Mainkur",
    subtitle:
      "Stellplätze auf einer Wiese in Maintal, mit Blick auf die Frankfurter Skyline. Familie Borgstedt empfängt Sie vom 1. April bis 30. September.",
    cta: "Platz buchen",
    welcome: "Familie Borgstedt · Maintal",
    explore: "Anfahrt",
    scrollHint: "Weiterlesen",
  },
  photoJourney: {
    moments: [
      {
        image: "/images/IMG_4482-scaled.jpg",
        alt: "Abendstimmung auf dem Campingplatz Mainkur",
        eyebrow: "Ankommen",
        title: "Hier beginnt die Auszeit",
        text: "Wenn die Sonne hinter den Bäumen versinkt und der Platz in warmes Licht getaucht ist – dann wissen Sie: Hier sind Sie richtig.",
      },
      {
        image: "/images/IMG_0920-scaled.jpg",
        alt: "Familien auf dem Campingplatz",
        eyebrow: "Gemeinsam",
        title: "Erinnerungen, die bleiben",
        text: "Kinder, die auf dem Spielplatz toben. Eltern, die endlich durchatmen. Nachbarn, die zum Plausch einladen. So fühlt sich Mainkur an.",
      },
      {
        image: "/images/IMG_0888.jpg",
        alt: "Natur und Ruhe auf dem Campingplatz",
        eyebrow: "Natur",
        title: "Weite Wiesen, alte Bäume, tiefe Ruhe",
        text: "Kein Lärm, kein Stress – nur das Rascheln der Blätter und der Blick ins Grüne. Ein Ort, an dem die Zeit langsamer wird.",
      },
    ],
  },
  floatingCta: {
    text: "Bereit für Ihren Platz in der Natur?",
    cta: "Jetzt buchen",
  },
  news: [
    {
      title: "Saisonpause – wir sind im Winterschlaf ❄️",
      body: `Unsere Saison ist beendet und der Campingplatz hat seine Tore geschlossen. Nun gönnen wir uns eine kleine Auszeit, legen die Füße hoch und sammeln neue Energie.

Ab dem 29. Oktober 2025 sind wir wieder erreichbar, um Ihre Anfragen und Buchungen zu beantworten. In dieser Zeit bereiten wir den Platz für die nächste Saison vor, damit alles frisch, gepflegt und startklar ist.

Bitte beachten Sie: Der Campingplatz öffnet erst wieder ab dem 01. April 2026. Dann erwarten Sie wie gewohnt erholsame Tage, viel Natur und eine entspannte Atmosphäre bei uns auf dem Platz.

Wir freuen uns schon jetzt auf ein Wiedersehen im Frühling!`,
      signature: "Ihre Familie Heike und Frank Borgstedt",
    },
    {
      title: "Voll ausgebucht: 22.–25. Mai 2026",
      body: `Liebe Campinggäste, wir freuen uns sehr über das große Interesse an unserem Campingplatz!

Bitte beachten Sie, dass wir im Zeitraum vom 22.05.2026 bis einschließlich 25.05.2026 bereits vollständig ausgebucht sind und leider keine freien Plätze mehr anbieten können. Für andere Zeiträume stehen wir Ihnen gerne weiterhin zur Verfügung.

Vielen Dank für Ihr Verständnis!`,
      signature: "Ihre Familie Heike und Frank Borgstedt",
    },
    {
      title: "Nur mit bestätigter Reservierung anreisen",
      body: `Liebe Campinggäste, wir freuen uns sehr über das große Interesse an unserem Platz in den Sommermonaten.

Damit Ihr Aufenthalt bei uns reibungslos beginnt, bitten wir Sie herzlich, nur mit einer bestätigten Reservierung anzureisen. Bitte beachten Sie außerdem, dass Anreisen nur bis spätestens 18:00 Uhr möglich sind.

Wichtiger Hinweis für Zeltgäste: Da wir keine separate Zeltwiese haben, können wir zusätzliche Gäste mit Zelt nur dann aufnehmen, wenn noch reguläre Stellplätze verfügbar sind.

Wir danken Ihnen für Ihr Verständnis und freuen uns auf Ihren Besuch!`,
      signature: "Ihre Familie Heike & Frank Borgstedt",
    },
  ],
  quickLinks: {
    title: "Wichtige Links",
    links: [
      { label: "Platzplan", href: "#platzplan", icon: "map" },
      { label: "Online-Buchung", href: "BOOKING", icon: "calendar" },
      { label: "Galerie", href: "#galerie", icon: "image" },
      { label: "Unser Campingplatz", href: "#unser-campingplatz", icon: "tent" },
    ],
  },
  sitePlan: {
    eyebrow: "Orientierung",
    title: "Platzplan",
    subtitle: "Stellplätze, Sanitäranlagen und Servicepunkte.",
    hint: "Tippen oder mit der Maus über einen Bereich fahren",
    legendTitle: "Bereich",
    compassNorth: "N",
    skylineHint: "Skyline Frankfurt",
    zoomIn: "Vergrößern",
    zoomOut: "Verkleinern",
    resetView: "Ansicht zurücksetzen",
  },
  facilities: {
    eyebrow: "Ausstattung",
    title: "Was Sie auf dem Platz finden",
    subtitle: "Gelände, Sanitär, Aufenthaltsraum, Kiosk und Dauerplätze.",
    learnMore: "Mehr erfahren",
    learnLess: "Weniger anzeigen",
    sitePlanLabel: "Platzplan",
    tabs: [
      {
        id: "gelaende",
        label: "Gelände",
        heading: "Gelände",
        summary: "Parkähnliche Wiese mit Skyline-Blick, Spielplatz und Tischtennis.",
        highlights: ["Skyline-Blick", "Spielplatz & Tischtennis", "Eingezäunt"],
        image: "/images/IMG_0930.jpg",
        sections: [
          {
            title: "Gelände",
            text: `Unser Campingplatz befindet sich auf einem parkähnlichen Wiesengelände mit altem Baumbestand, der an vielen Stellen für angenehmen Schatten sorgt. Hier können Sie entspannen und die einzigartige Atmosphäre genießen.

Ein besonderes Highlight: Der beeindruckende Blick auf die Skyline von Frankfurt, der Ihren Aufenthalt unvergesslich macht. Werfen Sie einen Blick auf unseren Platzplan, um sich vorab einen Eindruck zu verschaffen.`,
          },
          {
            title: "Spiel & Sport",
            text: `Für unsere kleinen Gäste bieten wir einen liebevoll gestalteten Spielplatz mit Rutsche, Wippe, Sandkasten, Schaukel und einem Wipptier. Hier können die Kinder nach Herzenslust toben und spielen.

Für sportliche Aktivitäten steht eine Tischtennisplatte bereit. Schläger und Bälle können Sie kostenfrei an der Rezeption ausleihen – Spaß für die ganze Familie garantiert!`,
          },
          {
            title: "Sicherheit",
            text: `Unser Gelände ist vollständig eingezäunt und nur für berechtigte Personen zugänglich. Jeder Gast erhält bei der Anreise einen eigenen Schlüssel, um jederzeit bequem Zugang zu haben.

Bitte beachten Sie, dass während der Nachtruhe (22:00 – 07:00 Uhr) das Befahren des Platzes nicht erlaubt ist, um die Ruhe aller Gäste zu gewährleisten.`,
          },
        ],
        sitePlan: true,
      },
      {
        id: "sanitaer",
        label: "Sanitär",
        heading: "Sanitäranlagen",
        summary: "Moderne Waschräume, Duschen, Waschmaschine und Spülküche.",
        highlights: ["Waschräume 2024 erneuert", "Duschen & Einzelkabinen", "Waschmaschine & Trockner"],
        image: "/images/IMG_7460-scaled.jpg",
        sections: [
          {
            title: "Sanitäranlagen",
            text: `Unsere Anlage bietet Ihnen gut ausgestattete Sanitärbereiche, die kontinuierlich modernisiert werden. Im Jahr 2024 wurden die Waschräume umfassend erneuert und verfügen nun über moderne Waschbecken sowie zwei Einzelkabinen, die zusätzliche Privatsphäre bieten. Auch unsere Duschen wurden neu gestaltet, um Ihnen ein komfortables und angenehmes Nutzungserlebnis zu ermöglichen.

Die Toiletten werden in den kommenden Jahren saniert, um den hohen Standard unserer Waschräume zu ergänzen. Für Chemietoiletten steht Ihnen ab sofort eine überdachte Entsorgungsstation im Außenbereich zur Verfügung.

Für die Reinigung Ihres Geschirrs bieten wir einen separaten Raum mit vier Spülbecken. Ihre Wäsche können Sie bequem in unserer Waschmaschine waschen und im Trockner trocknen. Für die Nutzung der Geräte benötigen Sie Wertmarken, die an der Rezeption erhältlich sind.`,
          },
        ],
      },
      {
        id: "aufenthaltsraum",
        label: "Aufenthaltsraum",
        heading: "Ausruhen mit Blick auf den Main",
        summary: "Gemütlicher Raum mit herrlichem Blick auf den Main – bei jedem Wetter.",
        highlights: ["Mainblick", "Gemütliche Sitzecke", "Bei jedem Wetter"],
        image: "/images/IMG_7603.jpg",
        sections: [
          {
            title: "Aufenthaltsraum",
            text: "Genießen Sie entspannte Stunden in unserem Aufenthaltsraum – mit herrlichem Blick auf den Main. Der ideale Ort, um bei jedem Wetter zur Ruhe zu kommen und den Tag ausklingen zu lassen.",
          },
        ],
      },
      {
        id: "shop",
        label: "Shop",
        heading: "Shop & Kiosk",
        summary: "Kiosk mit Grundversorgung, Getränken und frischen Brötchen auf Vorbestellung.",
        highlights: ["Getränke & Snacks", "Brötchenservice", "Supermärkte in 2,5 km"],
        image: "/images/IMG_7620-scaled.jpg",
        sections: [
          {
            title: "Shop",
            text: `Unser Kiosk bietet Ihnen eine Auswahl an Lebensmitteln und Alltagsartikeln – darunter gekühlte Getränke, Eis, Fertiggerichte und vieles mehr, alles zu fairen Preisen. Von Montag bis Samstag verwöhnen wir Sie außerdem mit frisch gebackenen Brötchen, die Sie bequem auf Vorbestellung erhalten.

Für größere Einkäufe stehen Ihnen im Umkreis von nur 2,5 km mehrere Supermärkte, Bäckereien und Metzgereien zur Verfügung, sodass Sie jederzeit bestens versorgt sind.`,
          },
        ],
      },
      {
        id: "dauercamping",
        label: "Dauercamping",
        heading: "Dauerstellplätze",
        summary: "Parzellierte Dauer- und Winterstellplätze für Wohnwagen und Wohnmobile.",
        highlights: ["80–120 m² Parzellen", "Winterabstellplatz", "Preise auf Anfrage"],
        image: "/images/IMG_0888.jpg",
        sections: [
          {
            title: "Dauerstellplätze pro Jahr",
            text: "Preise auf Anfrage",
            list: {
              included: [
                "ca. 80–120 m² großer parzellierter Stellplatz für Wohnwagen mit Vorzelt und PKW",
                "vom 1. April – 30. September Benutzung der Sanitäranlagen",
                "2 Personen",
                "Winterabstellplatz für die Zeit vom 1. Oktober – 31. März",
              ],
              excluded: [
                "Strom wird nach Verbrauch abgerechnet, die Kilowattstunde 0,85 €, jedoch Mindestabnahme 30 € pro Saison",
                "Müllentsorgung (1,50 € / Müllsack)",
              ],
            },
          },
          {
            title: "Abstellplätze für Wohnwagen / Wohnmobile pro Jahr",
            text: "Preise auf Anfrage",
          },
        ],
      },
    ],
  },
  experience: {
    eyebrow: "Der Platz",
    title: "Was Gäste bei uns schätzen",
    subtitle: "Wiese, Skyline, Spielplatz und der Main – das ist Mainkur.",
    items: [
      {
        key: "skyline",
        title: "Skyline-Blick",
        description: "Frankfurts Hochhäuser vom eigenen Stellplatz – einzigartig in der Region.",
      },
      {
        key: "meadow",
        title: "Parkähnliches Gelände",
        description: "Alte Bäume, weite Wiesen und viel Platz zum Durchatmen.",
      },
      {
        key: "family",
        title: "Für die ganze Familie",
        description: "Entspannt campen mit kurzen Wegen zu Spielplatz und Sanitär.",
      },
      {
        key: "playground",
        title: "Spiel & Spaß",
        description: "Spielplatz, Tischtennis und Platz zum Toben für die Kleinen.",
      },
      {
        key: "mainRiver",
        title: "Blick auf den Main",
        description: "Im Aufenthaltsraum den Fluss genießen – bei jedem Wetter.",
      },
      {
        key: "evening",
        title: "Abendstimmung",
        description: "Ruhe, Natur und ein Ort zum Ankommen nach einem erlebnisreichen Tag.",
      },
    ],
  },
  imageBanners: {
    skyline: {
      quote: "Stadt & Natur",
      title: "Camping bei Frankfurt – Skyline vor der Haustür",
      subtitle:
        "Übernachten bei Frankfurt und trotzdem in der Natur: Morgens in die Skyline, abends zurück auf der Wiese. Die Metropole ist nur eine kurze Fahrt entfernt – Ihr ruhiger Campingplatz bei Frankfurt wartet.",
    },
    nature: {
      quote: "Ruhe & Weite",
      title: "Hier bleibt die Zeit stehen",
      subtitle:
        "Weite Wiesen, schattenspendende Bäume und die Stille, die man in der Stadt vermisst. Genau das macht Mainkur so besonders.",
    },
  },
  surroundings: {
    eyebrow: "Umgebung",
    title: "In der Nähe",
    subtitle: "Frankfurt, Einkauf und die Schifffahrt auf dem Main sind schnell erreicht.",
    items: [
      {
        key: "frankfurt",
        name: "Frankfurt am Main",
        description:
          "Ideal zum Übernachten bei Frankfurt-Trips: Die Metropole mit Skyline, Museen und Altstadt ist schnell per Bus, Bahn oder Auto erreichbar.",
        distance: "ca. 15 km",
      },
      {
        key: "transport",
        name: "Öffentlicher Nahverkehr",
        description:
          "Frankfurt ist sehr gut mit Bus, Straßenbahn oder Bahn zu erreichen. Auch Hanau oder Offenbach ist direkt mit Bus und Bahn angeschlossen.",
        distance: "1,1 km",
      },
      {
        key: "bakery",
        name: "Bäcker & Einkauf",
        description:
          "Die nächste Bäckereifiliale hat sehr angenehme Öffnungszeiten. LIDL und weitere Geschäfte sind in wenigen Minuten erreichbar.",
        distance: "1,4 km",
      },
      {
        key: "mainShip",
        name: "Primus Linie",
        description:
          "Die Primus Linie ist mit 5 Schiffen Hessens größtes Schifffahrtsunternehmen. Mit einer langen Tradition bietet es neben Rundfahrten im Frankfurter Stadtgebiet auch Tages- und Eventfahrten an.",
        distance: "Anlegestelle Fechenheim 2,0 km",
      },
    ],
  },
  directions: {
    title: "Anfahrt",
    routes: [
      {
        via: "A661",
        steps: [
          "Über die A661 Ausfahrt Frankfurt Ost auf die rechte Spur einordnen, um auf die Bundesstraße B8 (Hanauer Landstraße) Richtung Hanau abzubiegen.",
          "Sie folgen dieser ca. 3,2 km an dem Autohaus Renault und den Baumärkten Hornbach und Bauhaus und der Kleinbrauerei Bierhannes vorbei, unter der Brücke hindurch vor der Ampel rechts abbiegen.",
        ],
      },
      {
        via: "A66",
        steps: [
          "Über die A66 Richtung Frankfurt, an der Ausfahrt Maintal-Bischofsheim rechts abbiegen Richtung Frankfurt.",
          "An der zweiten Ampel rechts abbiegen auf die Bundesstraße B8/40 (Frankfurter Landstraße).",
          "Nach ca. 1,5 km liegt der Campingplatz auf der linken Seite, an dem Sie vorbeifahren.",
          'Nach ca. 800 m links einordnen und um das Restaurant „Bud & Spencer" herum- und Richtung Maintal zurückfahren (beschildert).',
          "Nachdem Sie unter der Brücke hindurch gefahren sind, vor der Ampel rechts abbiegen.",
        ],
      },
    ],
  },
  openingHours: {
    title: "Öffnungszeiten",
    teaser: "Saison vom 1. April bis 30. September. Anreise täglich in den Rezeptionszeiten, reservierte Plätze ab 11 Uhr.",
    paragraphs: [
      "Sie können täglich zwischen 10:00 – 12:00 Uhr und 15:00 – 18:00 Uhr anreisen. Reservierte Stellplätze stehen Ihnen ab 11:00 Uhr zur Verfügung.",
      "Am Abreisetag ist der Stellplatz bis 11:00 Uhr zu räumen.",
      "Vom 1. Oktober bis 31. März ist unser Platz geschlossen!",
      "Unser Campingplatz ist ein Saisonbetrieb und ist vom 1. April bis 30. September geöffnet.",
      "Wir würden uns freuen, Sie demnächst bei uns willkommen zu heißen.",
    ],
    signature: "Ihre Familie Heike Borgstedt",
  },
  gallery: {
    eyebrow: "Fotos",
    title: "Fotos vom Platz",
    subtitle: "Stellplätze, Sanitäranlagen und der Blick über den Main.",
    featuredLabel: "Fotos",
    ctaText: "Wenn ein Foto passt, buchen Sie Ihren Stellplatz direkt.",
    cta: "Jetzt Platz buchen",
  },
  contact: {
    eyebrow: "Kontakt",
    title: "So erreichen Sie uns",
    teaser: "Heike und Frank Borgstedt sind in der Saison an der Rezeption für Sie da.",
    reachUs: "Sie erreichen uns täglich von 1. April – 30. September zwischen",
  },
  pricesPage: {
    title: "Preise",
    subtitle: "Preisliste 2026",
    partyHeading: "Reisegruppe",
    pitchesHeading: "Stellplätze",
    pitchesHint:
      "Wählen Sie in jeder Karte Ihre Reisegruppe – die Mengenwahl gilt für alle Kategorien gleichzeitig.",
    additionalHeading: "Weitere Preise",
    breakdownLabel: "Zusammensetzung pro Nacht",
    adults: "Erwachsene",
    children: "Kinder",
    dogs: "Hunde",
    electricity: "Strom",
    totalPerNight: "Gesamt pro Nacht",
    perNight: "/ Nacht",
    cheapestBadge: "Günstigster Preis",
    comparisonMoreThan: "Für {amount} / Nacht mehr als {category}",
    quickSelectBack: "Schnellauswahl",
    cta: "Jetzt online buchen",
    disclaimer: "Unverbindliche Beispielrechnung pro Nacht. Der endgültige Preis ergibt sich bei der Buchung.",
  },
  footer: {
    impressum: "Impressum",
    privacy: "Datenschutz",
    agb: "AGB",
    copyright: "Campingplatz Mainkur",
  },
  bookingHint: "Für eine Buchung nutzen Sie bitte unsere Online-Buchung.",
};
